import React from 'react';
import './AtlasMapLegend.css';

// Legend for styled Atlas maps, drawn from atlasMapRuntime.legendModel(config, theme):
//   {title, steps: {colors, labels, note} | null, categories: [{label, color, count}], items: [{kind, label, color, count, detail}]}
// layout 'strip' (the map card): a row between the finding and the map, so it never covers data (same as the PDF card).
// layout 'overlay': floats over the map's top-left corner (maps rendered without a card).

function hexToRgba(hex, alpha) {
  const h = String(hex || '').replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(h)) return `rgba(226, 99, 19, ${alpha})`;
  return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)}, ${alpha})`;
}

function formatCount(count) {
  const n = Number(count);
  if (count === null || count === undefined || count === '' || !Number.isFinite(n)) return null;
  return n.toLocaleString('en-US');
}

function ItemKey({ kind, color }) {
  if (kind === 'ring') {
    return <i className="atlas-map-legend-key atlas-map-legend-key--ring" style={{ boxShadow: `inset 0 0 0 1.6px ${color || 'currentColor'}` }} />;
  }
  if (kind === 'line') {
    return <i className="atlas-map-legend-key atlas-map-legend-key--line" style={color ? { borderTopColor: color } : undefined} />;
  }
  if (kind === 'subject') {
    const c = color || '#E26313';
    return (
      <i
        className="atlas-map-legend-key atlas-map-legend-key--subject"
        style={{ background: c, boxShadow: `0 0 0 2px var(--surface-primary), 0 0 0 5px ${hexToRgba(c, 0.22)}` }}
      />
    );
  }
  if (kind === 'area') {
    return <i className="atlas-map-legend-key atlas-map-legend-key--area" style={{ background: color }} />;
  }
  if (kind === 'outline') {
    return <i className="atlas-map-legend-key atlas-map-legend-key--area" style={{ background: 'transparent', boxShadow: `inset 0 0 0 2px ${color || '#E26313'}` }} />;
  }
  return <i className="atlas-map-legend-key atlas-map-legend-key--point" style={{ background: color }} />;
}

function Row({ kind, label, color, count, detail }) {
  const shown = formatCount(count);
  return (
    <div className="atlas-map-legend-row">
      <ItemKey kind={kind} color={color} />
      <span className="atlas-map-legend-label">
        {label}
        {detail ? <span className="atlas-map-legend-detail"> {detail}</span> : null}
      </span>
      {shown !== null ? <b>{shown}</b> : null}
    </div>
  );
}

export default function AtlasMapLegend({ model, layout = 'overlay' }) {
  if (!model) return null;
  const steps = model.steps && Array.isArray(model.steps.colors) && model.steps.colors.length > 0 ? model.steps : null;
  const categories = Array.isArray(model.categories) ? model.categories.filter(Boolean) : [];
  const allItems = Array.isArray(model.items) ? model.items.filter(Boolean) : [];
  const items = allItems.filter((it) => it.kind !== 'note');
  const notes = allItems.filter((it) => it.kind === 'note' && it.label);
  if (!steps && categories.length === 0 && items.length === 0 && notes.length === 0) return null;

  const k = steps ? steps.colors.length : 0;
  const labels = steps && Array.isArray(steps.labels) ? steps.labels : [];
  // k + 1 labels = class boundaries (centered under the joins); k labels = one per class.
  const labelLayout = labels.length === k + 1 ? 'boundaries' : labels.length === k ? 'classes' : null;

  // Every boundary label gets room for its own text (e.g. "$750k"), so the scale never runs them together.
  const longest = labels.reduce((n, l) => Math.max(n, String(l === null || l === undefined ? '' : l).length), 0);
  const scaleWidth = steps ? Math.max(layout === 'strip' ? 200 : 190, Math.round((labelLayout === 'boundaries' ? k + 1 : k) * (longest * 6.2 + 10))) : 0;
  const strip = layout === 'strip';

  return (
    <div
      className={`atlas-map-legend atlas-map-legend--${strip ? 'strip' : 'overlay'}${steps ? ' atlas-map-legend--steps' : ''}`}
      role="group"
      aria-label={model.title || 'Map legend'}
    >
      {model.title && !strip ? <div className="atlas-map-legend-title">{model.title}</div> : null}

      {steps ? (
        <div className="atlas-map-legend-scale" style={{ width: scaleWidth }}>
          {model.title && strip ? <div className="atlas-map-legend-title">{model.title}</div> : null}
          <div className="atlas-map-legend-steps" aria-hidden="true">
            {steps.colors.map((c, i) => (
              <span key={i} style={{ background: c }} />
            ))}
          </div>
          {labelLayout ? (
            <div
              className={`atlas-map-legend-breaks atlas-map-legend-breaks--${labelLayout}`}
              style={{ gridTemplateColumns: `repeat(${labels.length}, minmax(0, 1fr))` }}
            >
              {labels.map((l, i) => (
                <span key={i}>{l === null || l === undefined ? '' : String(l)}</span>
              ))}
            </div>
          ) : null}
          {steps.note ? <div className="atlas-map-legend-note">{steps.note}</div> : null}
        </div>
      ) : null}

      {!steps && model.title && strip && categories.length > 0 ? <div className="atlas-map-legend-title">{model.title}</div> : null}

      {categories.length > 0 ? (
        <div className="atlas-map-legend-rows">
          {categories.map((c, i) => (
            <Row key={`c_${i}`} kind="area" label={c.label} color={c.color} count={c.count} />
          ))}
        </div>
      ) : null}

      {items.length > 0 ? (
        <div className="atlas-map-legend-rows">
          {items.map((it, i) => (
            <Row key={`i_${i}`} kind={it.kind} label={it.label} color={it.color} count={it.count} detail={it.detail} />
          ))}
        </div>
      ) : null}

      {notes.length > 0 ? (
        <div className="atlas-map-legend-rows atlas-map-legend-notes">
          {notes.map((it, i) => (
            <span key={`n_${i}`} className="atlas-map-legend-chip">{it.label}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
